// Bell icon dropdown shown next to the user's name in the app header.
// Polls /notifications/unread-count every 30s; opens the panel on
// click and fetches the latest 50 items. Each row is click-to-mark-
// read + (optional) follow-CTA.
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  listNotifications,
  unreadNotificationCount,
  markNotificationRead,
  markNotificationsViewed,
  markAllNotificationsRead,
  dismissNotification,
} from 'api/notifications/notifications';

const POLL_MS = 30 * 1000;

function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

function typeIcon(type) {
  switch (type) {
    case 'BRAND_ENRICHED': return '✨';
    case 'SOCIAL_CONNECTED': return '🔌';
    case 'SOCIAL_DISCONNECTED': return '🔌';
    case 'BRAND_ENRICHMENT_FAILED': return '⚠️';
    case 'TEAM_ACTIVITY': return '👥';
    default: return '🔔';
  }
}

export default function NotificationBell() {
  const userInfo = useSelector((s) => s.user?.userInfo) || {};
  const history = useHistory();
  const isSuperAdmin = !!userInfo.superAdmin;

  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState('mine');
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const mountedRef = useRef(true);

  const refreshCount = useCallback(async () => {
    try {
      const r = await unreadNotificationCount({ scope: 'mine' });
      if (mountedRef.current) setUnread(typeof r.count === 'number' ? r.count : 0);
    } catch (e) {
      // Quietly ignore — the bell is decorative when the API is
      // unreachable (e.g., user just signed out and the token rotated).
    }
  }, []);

  const refreshList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listNotifications({ scope, limit: 50 });
      const list = Array.isArray(r.items) ? r.items : [];
      if (mountedRef.current) setItems(list);
      // The rows are now on screen → mark the still-unengaged ones as
      // viewed so they drop out of the badge. The backend only stamps the
      // caller's own rows, so this is a no-op for other users' rows shown
      // in company/all scope. Re-sync the count once the server confirms.
      const unseenIds = list
        .filter((i) => i.status === 'unread' && !i.viewedAt && !i.clickedAt)
        .map((i) => i._id);
      if (unseenIds.length) {
        try {
          await markNotificationsViewed(unseenIds);
          refreshCount();
        } catch (e) {
          // Ignore — the badge stays as-is until the next poll.
        }
      }
    } catch (e) {
      // Ignore — keep last good list.
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [scope, refreshCount]);

  useEffect(() => {
    mountedRef.current = true;
    refreshCount();
    const id = setInterval(refreshCount, POLL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
    };
  }, [refreshCount]);

  useEffect(() => {
    if (open) refreshList();
  }, [open, scope, refreshList]);

  // Close on outside-click.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const onItemClick = async (n) => {
    // Optimistic: mark read locally, then fire-and-forget the API call.
    if (n.status === 'unread') {
      setItems((prev) => prev.map((x) => (x._id === n._id ? { ...x, status: 'read' } : x)));
      setUnread((c) => Math.max(0, c - 1));
      markNotificationRead(n._id).catch(() => {});
    }
    if (n.ctaHref) {
      setOpen(false);
      // Internal hrefs use the router; external go through window.location.
      if (n.ctaHref.startsWith('/')) history.push(n.ctaHref);
      else window.location.href = n.ctaHref;
    }
  };

  const onDismissClick = async (e, n) => {
    e.stopPropagation();
    setItems((prev) => prev.filter((x) => x._id !== n._id));
    if (n.status === 'unread') setUnread((c) => Math.max(0, c - 1));
    dismissNotification(n._id).catch(() => {});
  };

  const onMarkAllRead = async () => {
    setItems((prev) => prev.map((x) => ({ ...x, status: x.status === 'unread' ? 'read' : x.status })));
    setUnread(0);
    markAllNotificationsRead().catch(() => {});
  };

  const visibleItems = items.filter((i) => i.status !== 'dismissed');

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'inline-block', marginRight: 8 }}>
      <button
        type="button"
        aria-label="Notifications"
        onClick={() => setOpen((o) => !o)}
        style={{
          background: 'transparent',
          border: 'none',
          padding: '6px 8px',
          position: 'relative',
          cursor: 'pointer',
          fontSize: 20,
          lineHeight: 1,
        }}
        title="Notifications"
      >
        <span role="img" aria-label="bell">🔔</span>
        {unread > 0 && (
          <span
            data-testid="bell-unread-badge"
            style={{
              position: 'absolute',
              top: -2,
              right: -2,
              background: '#e63946',
              color: 'white',
              fontSize: 10,
              fontWeight: 700,
              padding: '1px 5px',
              borderRadius: 10,
              minWidth: 16,
              textAlign: 'center',
              lineHeight: '14px',
            }}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 6px)',
            width: 360,
            maxHeight: 480,
            overflowY: 'auto',
            background: 'white',
            border: '1px solid #e0e0e0',
            borderRadius: 8,
            boxShadow: '0 6px 24px rgba(0,0,0,0.08)',
            zIndex: 2000,
          }}
        >
          <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #eee' }}>
            <strong style={{ fontSize: 14 }}>Notifications</strong>
            <div className="d-flex align-items-center" style={{ gap: 8, fontSize: 12 }}>
              <select
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                style={{ fontSize: 12, padding: '2px 4px' }}
                title="Scope"
              >
                <option value="mine">Mine</option>
                <option value="company">My company</option>
                {isSuperAdmin && <option value="all">All companies</option>}
              </select>
              <button
                type="button"
                onClick={onMarkAllRead}
                disabled={unread === 0}
                style={{ background: 'transparent', border: 'none', color: '#3a6ea5', cursor: unread === 0 ? 'default' : 'pointer', fontSize: 12 }}
              >
                Mark all read
              </button>
            </div>
          </div>
          {loading && (
            <div style={{ padding: 16, textAlign: 'center', fontSize: 13, color: '#888' }}>Loading…</div>
          )}
          {!loading && visibleItems.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: '#888' }}>
              You&apos;re all caught up.
            </div>
          )}
          {!loading && visibleItems.map((n) => (
            <div
              key={n._id}
              onClick={() => onItemClick(n)}
              style={{
                padding: '10px 12px',
                display: 'flex',
                gap: 10,
                borderBottom: '1px solid #f4f4f4',
                background: n.status === 'unread' ? '#f7faff' : 'white',
                cursor: n.ctaHref || n.status === 'unread' ? 'pointer' : 'default',
              }}
            >
              <span style={{ fontSize: 18, flex: '0 0 auto' }}>{typeIcon(n.type)}</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: n.status === 'unread' ? 600 : 500 }}>
                  {n.title}
                </div>
                {n.body && (
                  <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>{n.body}</div>
                )}
                <div className="d-flex align-items-center justify-content-between" style={{ marginTop: 4 }}>
                  <span style={{ fontSize: 11, color: '#999' }}>{timeAgo(n.createdAt)}</span>
                  {n.ctaLabel && (
                    <span style={{ fontSize: 11, color: '#3a6ea5' }}>{n.ctaLabel} →</span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={(e) => onDismissClick(e, n)}
                aria-label="Dismiss"
                title="Dismiss"
                style={{ background: 'transparent', border: 'none', color: '#bbb', cursor: 'pointer', fontSize: 14, padding: 0, marginLeft: 4 }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
